import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { API_BASE } from '../api';
import Layout from '../components/Layout';
import { useToast } from '../components/ToastContext';
import { T } from '../styles/theme';
import ProGate from '../components/ProGate';
import { useIsMobile } from '../hooks/useIsMobile';

export default function Profilo() {
  const toast = useToast();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [ebayStatus, setEbayStatus] = useState(null);
  const [vintedInstalled, setVintedInstalled] = useState(() => localStorage.getItem('vinted_extension_installed') === 'true');
  const [showExtGuide, setShowExtGuide] = useState(false);
  const [extGuideStep, setExtGuideStep] = useState(1);
  const [extVersion, setExtVersion] = useState(null);
  const [showChangelog, setShowChangelog] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get('/auth/profile').then((r) => setUser(r.data)).catch(() => {});
    api.get('/stats').then((r) => setStats(r.data)).catch(() => {});
    api.get('/subscription/status').then((r) => setSubscription(r.data)).catch(() => {});
    api.get('/auth/ebay-status').then((r) => setEbayStatus(r.data)).catch(() => {});
    api.get('/download/estensione-version').then((r) => setExtVersion(r.data)).catch(() => {});

    const ebayResult = searchParams.get('ebay');
    if (ebayResult === 'success') {
      toast.success('eBay collegato con successo!');
      searchParams.delete('ebay');
      setSearchParams(searchParams, { replace: true });
      api.get('/auth/ebay-status').then((r) => setEbayStatus(r.data)).catch(() => {});
    } else if (ebayResult === 'error') {
      const msg = searchParams.get('msg') || 'Errore sconosciuto';
      toast.error(`Errore collegamento eBay: ${msg}`);
      searchParams.delete('ebay');
      searchParams.delete('msg');
      setSearchParams(searchParams, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [showManualCode, setShowManualCode] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [manualSaving, setManualSaving] = useState(false);
  const [ebayConnecting, setEbayConnecting] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === 'ebay_oauth') {
        setEbayConnecting(false);
        setShowManualCode(false);
        if (e.data.ebay === 'success') {
          toast.success('eBay collegato con successo!');
          api.get('/auth/ebay-status').then((r) => setEbayStatus(r.data)).catch(() => {});
        } else {
          toast.error(`Errore eBay: ${e.data.msg || 'Sconosciuto'}`);
        }
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [toast]);

  const handleConnectEbay = async () => {
    // Apri il popup SUBITO (sincrono) per evitare il popup blocker di Chrome
    const w = 600, h = 700;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    const popup = window.open('about:blank', 'ebay_auth', `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`);
    if (!popup) {
      setShowManualCode(true);
      toast.info('Popup bloccata. Usa il campo manuale sotto.');
      return;
    }
    setEbayConnecting(true);
    try {
      const r = await api.get('/auth/ebay-link');
      if (!popup.closed) {
        popup.location.href = r.data.url;
      }
      setTimeout(() => {
        if (!popup || popup.closed) {
          setEbayConnecting(false);
          setShowManualCode(true);
          toast.info('Popup bloccata. Usa il campo manuale sotto.');
        }
      }, 3000);
      setTimeout(() => { setShowManualCode(true); }, 15000);
    } catch {
      if (popup && !popup.closed) popup.close();
      toast.error('Errore generazione link eBay');
      setEbayConnecting(false);
    }
  };

  const handleManualCode = async () => {
    if (!manualCode.trim()) return;
    setManualSaving(true);
    try {
      await api.post('/auth/ebay-exchange', { code: manualCode.trim() });
      toast.success('eBay collegato con successo!');
      setShowManualCode(false);
      setManualCode('');
      setEbayConnecting(false);
      api.get('/auth/ebay-status').then((r) => setEbayStatus(r.data)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Codice non valido o scaduto');
    } finally { setManualSaving(false); }
  };

  const handleDownloadExtension = () => {
    window.open(`${API_BASE}/download/estensione`, '_blank');
    setShowExtGuide(true);
    setExtGuideStep(1);
  };

  async function scaricaEstensione() {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Sessione scaduta. Rifai login.');
      return;
    }
    try {
      setDownloading(true);
      // Cache-buster + no-store: evita che il browser serva uno ZIP vecchio dalla cache HTTP
      const res = await fetch(`${API_BASE}/api/estensione/download?t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      });
      if (!res.ok) {
        if (res.status === 403) {
          toast.error('Solo utenti PRO possono scaricare l\'estensione. Upgrade al piano PRO!');
        } else if (res.status === 404) {
          toast.error('File estensione temporaneamente non disponibile. Riprova tra poco.');
        } else {
          toast.error('Errore download: HTTP ' + res.status);
        }
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `impero-elite-extension-v${extVersion?.version || '2.9.7'}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Errore download: ' + e.message);
    } finally {
      setDownloading(false);
    }
  }

  const handleExtInstalled = () => {
    localStorage.setItem('vinted_extension_installed', 'true');
    setVintedInstalled(true);
    setShowExtGuide(false);
    toast.success('Estensione Vinted attivata!');
  };

  const handleStripePortal = async () => {
    setPortalLoading(true);
    try {
      const r = await api.get('/stripe/portal');
      const url = r.data?.portal_url || r.data?.url;
      if (url) window.location.href = url;
      else toast.error('Errore apertura portale Stripe');
    } catch { toast.error('Errore apertura portale Stripe'); }
    finally { setPortalLoading(false); }
  };

  const avatarUrl = user?.discord_id && user?.discord_avatar
    ? `https://cdn.discordapp.com/avatars/${user.discord_id}/${user.discord_avatar}.png?size=128`
    : null;

  const ruoloBadge = {
    admin: { bg: 'rgba(251,191,36,0.15)', color: '#fbbf24', label: 'ADMIN' },
    collaboratore: { bg: 'rgba(74,222,128,0.15)', color: '#4ade80', label: 'COLLAB' },
    pro: { bg: 'rgba(167,139,250,0.15)', color: '#a78bfa', label: 'PRO' },
    free: { bg: 'rgba(107,114,128,0.2)', color: '#6b7280', label: 'FREE' },
  };
  const badge = ruoloBadge[user?.ruolo] || ruoloBadge.free;

  const InfoRow = ({ label, value, accent }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${T.border}` }}>
      <span style={{ fontSize: '0.82rem', color: T.muted }}>{label}</span>
      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: accent || T.text }}>{value || '—'}</span>
    </div>
  );

  return (
    <Layout>
      <main style={{ maxWidth: 720, margin: '0 auto', padding: isMobile ? '12px 16px 80px' : '32px 24px' }}>
        <h1 style={{ fontFamily: T.fontDisplay, fontSize: isMobile ? '1.3rem' : '1.6rem', fontWeight: 800, color: T.text, marginBottom: 20 }}>Profilo</h1>

        {/* Card profilo Discord */}
        {user && (
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: isMobile ? 20 : 28, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
              {avatarUrl ? (
                <img src={avatarUrl} alt="" style={{ width: 64, height: 64, borderRadius: 16, objectFit: 'cover', border: `2px solid ${T.border}` }} />
              ) : (
                <div style={{ width: 64, height: 64, borderRadius: 16, background: T.gradPrimary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontWeight: 800, fontSize: '1.3rem', color: '#fff' }}>{(user.name || '?').charAt(0).toUpperCase()}</span>
                </div>
              )}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <h2 style={{ fontFamily: T.fontDisplay, fontSize: '1.15rem', fontWeight: 700, color: T.text, margin: 0 }}>{user.discord_username || user.name}</h2>
                  <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: badge.bg, color: badge.color, letterSpacing: '0.04em' }}>{badge.label}</span>
                </div>
                {user.discord_id && (
                  <p style={{ fontSize: '0.78rem', color: T.muted, marginTop: 4, fontFamily: T.fontMono }}>ID: {user.discord_id}</p>
                )}
              </div>
            </div>

            <div style={{ background: T.elem, borderRadius: 12, padding: '4px 16px' }}>
              <InfoRow label="Nome utente" value={user.discord_username || user.name} />
              <InfoRow label="Membro dal" value={user.created_at ? new Date(user.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' }) : null} />
              <InfoRow label="Piano attivo" value={badge.label} accent={badge.color} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' }}>
                <span style={{ fontSize: '0.82rem', color: T.muted }}>Prodotti totali</span>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: T.text }}>{stats ? (stats.prodotti_disponibili + stats.prodotti_venduti) : '—'}</span>
              </div>
            </div>
          </div>
        )}

        {/* Card Abbonamento Stripe — solo se ha stripe */}
        {subscription?.has_stripe && (
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: isMobile ? 20 : 28, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <span style={{ fontSize: '1.2rem' }}>💳</span>
              <h3 style={{ fontFamily: T.fontDisplay, fontSize: '1rem', fontWeight: 700, color: T.text, margin: 0 }}>Abbonamento Stripe</h3>
              <span style={{
                fontSize: '0.6rem', fontWeight: 700, padding: '2px 8px', borderRadius: 6, marginLeft: 'auto',
                background: subscription.stato === 'active' ? 'rgba(74,222,128,0.15)' : 'rgba(248,113,113,0.15)',
                color: subscription.stato === 'active' ? '#4ade80' : '#f87171',
              }}>{subscription.stato === 'active' ? 'ATTIVO' : subscription.stato?.toUpperCase()}</span>
            </div>

            <div style={{ background: T.elem, borderRadius: 12, padding: '4px 16px' }}>
              <InfoRow label="Piano" value="PRO" accent="#a78bfa" />
              <InfoRow label="Prezzo" value="35,00 euro/mese" />
              {subscription.scadenza && (
                <InfoRow
                  label={subscription.stato === 'active' ? 'Prossimo rinnovo' : 'Scadenza'}
                  value={new Date(subscription.scadenza).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}
                  accent={subscription.stato === 'active' ? '#4ade80' : '#f87171'}
                />
              )}
              <div style={{ padding: '10px 0' }}>
                <span style={{ fontSize: '0.82rem', color: T.muted }}>Gestisci abbonamento</span>
              </div>
            </div>

            <button
              onClick={handleStripePortal}
              disabled={portalLoading}
              style={{
                width: '100%', marginTop: 16, padding: '11px 20px', borderRadius: 10,
                background: 'linear-gradient(135deg, rgba(167,139,250,0.12), rgba(79,70,229,0.08))',
                border: `1px solid rgba(167,139,250,0.3)`,
                color: '#a78bfa', fontSize: '0.85rem', fontWeight: 600, fontFamily: T.fontBody,
                cursor: 'pointer', transition: '0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(167,139,250,0.2), rgba(79,70,229,0.12))'; e.currentTarget.style.borderColor = '#7c3aed'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(167,139,250,0.12), rgba(79,70,229,0.08))'; e.currentTarget.style.borderColor = 'rgba(167,139,250,0.3)'; }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
              {portalLoading ? 'Caricamento...' : 'Gestisci su Stripe'}
            </button>
          </div>
        )}

        {/* Piattaforme */}
        <ProGate feature="Piattaforme collegate">
        <div className="card" style={{ padding: isMobile ? 20 : 24, marginBottom: 20 }}>
          <h3 className="section-title">Piattaforme collegate</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 rounded-xl" style={{ background: T.elem }}>
              <div className="flex items-center gap-3">
                <span className="font-bold text-lg" style={{ color: T.violet }}>eBay</span>
                <span className={`badge ${ebayStatus?.collegato ? 'badge-green' : ''}`} style={!ebayStatus?.collegato ? { background: 'rgba(107,114,128,0.2)', color: T.muted } : {}}>
                  {ebayStatus?.collegato ? 'Collegato' : 'Non collegato'}
                </span>
                {ebayStatus?.collegato && ebayStatus?.username && (
                  <span className="text-xs" style={{ color: T.faint }}>@{ebayStatus.username}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {ebayStatus?.collegato && ebayStatus?.scadenza && (
                  <span className="text-xs hidden sm:inline" style={{ color: T.faint }}>Scade: {new Date(ebayStatus.scadenza).toLocaleDateString('it-IT')}</span>
                )}
                {ebayStatus?.collegato && (
                  <button onClick={async () => {
                    try {
                      await api.delete('/auth/ebay-disconnect');
                      setEbayStatus({ collegato: false });
                      toast.success('eBay scollegato');
                    } catch { toast.error('Errore scollegamento'); }
                  }} className="btn-ghost text-sm" style={{ color: T.red, borderColor: `${T.red}33` }}>
                    Scollega
                  </button>
                )}
                <button onClick={handleConnectEbay} disabled={ebayConnecting}
                  className={ebayStatus?.collegato ? 'btn-ghost text-sm' : 'btn-primary text-sm'}>
                  {ebayConnecting ? 'In corso...' : ebayStatus?.collegato ? 'Rinnova' : 'Collega eBay'}
                </button>
              </div>
            </div>
            {showManualCode && !ebayStatus?.collegato && (
              <div className="rounded-xl p-4" style={{ background: `${T.elem}80`, border: `1px solid ${T.border}` }}>
                <p className="text-sm mb-2" style={{ color: T.text }}>Se il redirect non funziona automaticamente, copia il <strong style={{ color: '#fff' }}>codice</strong> dall'URL della pagina eBay (il valore dopo <code style={{ color: T.violet }}>code=</code>) e incollalo qui:</p>
                <div className="flex gap-2">
                  <input type="text" value={manualCode} onChange={(e) => setManualCode(e.target.value)}
                    className="input flex-1 text-sm" placeholder="Incolla il codice eBay qui..." />
                  <button onClick={handleManualCode} disabled={manualSaving || !manualCode.trim()}
                    className="btn-primary text-sm whitespace-nowrap">
                    {manualSaving ? '...' : 'Collega'}
                  </button>
                </div>
              </div>
            )}
            <div className="p-4 rounded-xl" style={{ background: T.elem }}>
              <div className="flex items-center gap-3 mb-3">
                <span className="font-bold text-lg" style={{ color: T.cyan }}>Vinted</span>
                {vintedInstalled && <span className="badge badge-green">Estensione attiva</span>}
              </div>
              {!vintedInstalled ? (
                <div>
                  <p className="text-sm mb-3" style={{ color: T.text }}>Per pubblicare su Vinted installa l'estensione Chrome gratuita</p>
                  <div className="space-y-2 mb-4 text-sm" style={{ color: T.muted }}>
                    <p>1. Scarica l'estensione (10 secondi)</p>
                    <p>2. Aprila in Chrome (30 secondi)</p>
                    <p>3. Pubblica con un click!</p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button onClick={handleDownloadExtension} className="btn-primary text-sm">Scarica Estensione</button>
                    <button onClick={handleExtInstalled} className="btn-ghost text-sm">Ho gia installato</button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs" style={{ color: T.muted }}>L'estensione e pronta. Quando crei un annuncio e clicchi "Pubblica su Vinted", si aprira Vinted con tutto gia compilato.</p>
                  {extVersion && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs" style={{ color: T.faint }}>Versione: v{extVersion.version} — Aggiornata</span>
                      <button onClick={() => setShowChangelog(!showChangelog)} className="text-xs hover:underline" style={{ color: T.cyan }}>
                        {showChangelog ? 'Nascondi note' : 'Note versione'}
                      </button>
                    </div>
                  )}
                  {showChangelog && extVersion?.note && (
                    <div className="rounded-lg p-3" style={{ background: `${T.card}80`, border: `1px solid ${T.border}` }}>
                      <p className="text-xs font-medium mb-1.5" style={{ color: T.text }}>Note versione v{extVersion.version}:</p>
                      <ul className="text-xs space-y-0.5" style={{ color: T.muted }}>
                        {extVersion.note.map((n, i) => <li key={i}>• {n}</li>)}
                      </ul>
                    </div>
                  )}
                  <button onClick={scaricaEstensione} disabled={downloading} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    width: '100%', padding: '10px 16px', borderRadius: 10,
                    background: 'rgba(34,211,238,0.08)', border: `1px solid rgba(34,211,238,0.25)`,
                    color: T.cyan, fontSize: '0.85rem', fontWeight: 600, fontFamily: T.fontBody,
                    cursor: downloading ? 'not-allowed' : 'pointer', transition: '0.2s',
                    opacity: downloading ? 0.6 : 1,
                  }}
                  onMouseEnter={e => { if (!downloading) { e.currentTarget.style.background = 'rgba(34,211,238,0.15)'; e.currentTarget.style.borderColor = T.cyan; } }}
                  onMouseLeave={e => { if (!downloading) { e.currentTarget.style.background = 'rgba(34,211,238,0.08)'; e.currentTarget.style.borderColor = 'rgba(34,211,238,0.25)'; } }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    {downloading ? 'Download in corso...' : 'Aggiorna o reinstalla estensione'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        </ProGate>

        {/* Extension Install Guide Modal */}
        {showExtGuide && (
          <div className="fixed inset-0 flex items-center justify-center z-50 px-4" style={{ background: 'rgba(13,11,30,0.95)', backdropFilter: 'blur(8px)' }}>
            <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 20 }} className="p-6 max-w-md w-full shadow-2xl">
              <h3 className="font-bold text-lg mb-5" style={{ color: T.violet }}>Installa l'estensione Vinted</h3>
              <div className="flex gap-2 mb-6">
                {[1,2,3,4].map(s => (
                  <div key={s} className="flex-1 h-1.5 rounded-full" style={{ background: extGuideStep >= s ? T.violetDark : T.border }} />
                ))}
              </div>
              {extGuideStep === 1 && (
                <div className="text-center space-y-3">
                  <div className="text-5xl mb-2">📥</div>
                  <p className="font-medium" style={{ color: T.text }}>Il file e stato scaricato</p>
                  <p className="text-sm" style={{ color: T.muted }}>Aprilo dalla cartella Download ed estrai il contenuto</p>
                </div>
              )}
              {extGuideStep === 2 && (
                <div className="text-center space-y-3">
                  <div className="text-5xl mb-2">🧩</div>
                  <p className="font-medium" style={{ color: T.text }}>Apri Chrome Extensions</p>
                  <p className="text-sm mb-2" style={{ color: T.muted }}>Vai su chrome://extensions</p>
                  <button onClick={() => { navigator.clipboard.writeText('chrome://extensions'); toast.info('Link copiato!'); }}
                    className="text-xs hover:underline" style={{ color: T.violet }}>Copia link</button>
                  <p className="text-sm mt-2" style={{ color: T.muted }}>Attiva la <strong style={{ color: T.text }}>Modalita sviluppatore</strong> in alto a destra</p>
                </div>
              )}
              {extGuideStep === 3 && (
                <div className="text-center space-y-3">
                  <div className="text-5xl mb-2">📂</div>
                  <p className="font-medium" style={{ color: T.text }}>Carica l'estensione</p>
                  <p className="text-sm" style={{ color: T.muted }}>Clicca <strong style={{ color: T.text }}>"Carica estensione non pacchettizzata"</strong></p>
                  <p className="text-sm" style={{ color: T.muted }}>Seleziona la cartella estratta dal download</p>
                </div>
              )}
              {extGuideStep === 4 && (
                <div className="text-center space-y-3">
                  <div className="text-5xl mb-2">✅</div>
                  <p className="font-bold text-lg" style={{ color: T.text }}>Fatto!</p>
                  <p className="text-sm" style={{ color: T.muted }}>L'estensione e installata e pronta all'uso</p>
                </div>
              )}
              <div className="flex gap-3 mt-6">
                {extGuideStep > 1 && (
                  <button onClick={() => setExtGuideStep(s => s - 1)} className="btn-ghost text-sm">← Indietro</button>
                )}
                {extGuideStep < 4 ? (
                  <button onClick={() => setExtGuideStep(s => s + 1)} className="btn-primary flex-1 text-sm">Avanti →</button>
                ) : (
                  <button onClick={handleExtInstalled} className="btn-primary flex-1 text-sm">Ho installato l'estensione</button>
                )}
                <button onClick={() => setShowExtGuide(false)} className="px-4 py-2 text-sm" style={{ color: T.faint }}>Chiudi</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </Layout>
  );
}
